import { useTranslation } from 'next-i18next'
import { makeStyles, Container, Grid } from '@material-ui/core'
import T, { specularRing, specularHost, specularRingReduced } from './tokens'
import Reveal from './Reveal'
import Link from '../utility/CustomLink'

const services = [
  {
    img: '/static/img/home-new/services/domestic.png',
    w: 462,
    h: 462,
    titleKey: 'businessServices.domestic.title',
    descKey: 'businessServices.domestic.desc',
  },
  {
    img: '/static/img/home-new/services/international.png',
    w: 544,
    h: 407,
    titleKey: 'businessServices.international.title',
    descKey: 'businessServices.international.desc',
  },
  {
    img: '/static/img/home-new/services/online-shop.png',
    w: 462,
    h: 346,
    titleKey: 'businessServices.onlineShop.title',
    descKey: 'businessServices.onlineShop.desc',
  },
  {
    img: '/static/img/home-new/services/high-volume.png',
    w: 469,
    h: 353,
    titleKey: 'businessServices.corporate.title',
    descKey: 'businessServices.corporate.desc',
  },
  {
    img: '/static/img/home-new/services/fulfillment.png',
    w: 484,
    h: 363,
    titleKey: 'businessServices.fulfillment.title',
    descKey: 'businessServices.fulfillment.desc',
  },
  {
    img: '/static/img/home-new/services/api.png',
    w: 467,
    h: 350,
    titleKey: 'businessServices.api.title',
    descKey: 'businessServices.api.desc',
  },
]

const useStyles = makeStyles((theme) => ({
  root: {
    padding: '70px 0',
    background: T.color.surface,
    [theme.breakpoints.down('sm')]: {
      padding: '40px 0',
    },
  },
  heading: {
    fontFamily: T.font.heading,
    fontSize: 32,
    fontWeight: 500,
    color: T.color.ink,
    textAlign: 'center',
    margin: '0 0 8px',
    '& span': {
      color: T.color.brand,
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: 22,
    },
  },
  subtitle: {
    fontFamily: T.font.body,
    fontSize: 16,
    color: T.color.muted,
    textAlign: 'center',
    margin: '0 0 16px',
    [theme.breakpoints.down('sm')]: {
      fontSize: 14,
    },
  },
  ctaWrapper: {
    textAlign: 'center',
    marginBottom: 40,
  },
  // Part of the shared "specular ring" CTA family (tokens.js) — another
  // solid blue-filled pill link, so it gets the same bright blue ring as
  // the check-price submit / track / header corporate buttons.
  ctaBtn: {
    ...specularHost,
    display: 'inline-block',
    fontFamily: T.font.heading,
    fontSize: 16,
    fontWeight: 500,
    color: T.color.onBrand,
    background: T.gradient.blue,
    padding: '12px 32px',
    borderRadius: T.radius.pill,
    textDecoration: 'none',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    boxShadow: '0 4px 16px rgba(0,102,204,0.25)',
    '&::before': specularRing(T.ring.blue),
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 6px 24px rgba(0,102,204,0.35)',
    },
    '@media (prefers-reduced-motion: reduce)': {
      ...specularRingReduced,
    },
  },
  cardLink: {
    display: 'block',
    height: '100%',
    textDecoration: 'none',
    color: 'inherit',
  },
  card: {
    background: T.color.surface,
    borderRadius: T.radius.lg,
    overflow: 'hidden',
    boxShadow: T.shadow.card,
    height: '100%',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-6px)',
      boxShadow: T.shadow.lg,
    },
  },
  cardImg: {
    width: '100%',
    height: 200,
    objectFit: 'cover',
    display: 'block',
    [theme.breakpoints.down('sm')]: {
      height: 180,
    },
  },
  cardBody: {
    padding: '20px 24px',
  },
  cardTitle: {
    fontFamily: T.font.heading,
    // Mockup (home.png @1920): 6-card title cap-height ~23px → ~19.4px in code
    // scale (mockup drawn 1.1875x) → 20px. Title color is brand blue (#009EFF,
    // sampled #008eff), not the previous dark ink.
    fontSize: 20,
    fontWeight: 500,
    color: T.color.brand,
    margin: '0 0 8px',
  },
  cardDesc: {
    fontFamily: T.font.body,
    // Mockup: desc cap-height ~15px → ~12.6px in code scale → 13px. Text is a
    // dark grey (#292929, sampled), darker than the previous muted #666.
    fontSize: 13,
    color: '#292929',
    lineHeight: 1.6,
    margin: 0,
  },
}))

const BusinessServicesNew = () => {
  const classes = useStyles()
  const { t } = useTranslation('home')

  return (
    <section className={classes.root}>
      <Container maxWidth={false} style={{ maxWidth: T.container }}>
        <Reveal>
          <h2 className={classes.heading}>
            <span>SHIPPOP</span> {t('businessServices.headingAfter')}
          </h2>
          <p className={classes.subtitle}>
            {t('businessServices.subtitle')}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className={classes.ctaWrapper}>
            <Link href="/business" passHref>
              <a className={classes.ctaBtn}>{t('businessServices.cta')}</a>
            </Link>
          </div>
        </Reveal>
        <Grid container spacing={3}>
          {services.map((s, i) => (
            <Grid item xs={12} sm={6} md={4} key={s.titleKey}>
              <Reveal delay={i * 0.1}>
                <Link href="/business" passHref>
                  <a className={classes.cardLink}>
                <div className={classes.card}>
                  <picture style={{ display: 'contents' }}>
                    <source
                      srcSet={s.img.replace(/\.png$/, '.webp')}
                      type="image/webp"
                    />
                    <img
                      src={s.img}
                      alt={t(s.titleKey)}
                      className={classes.cardImg}
                      width={s.w}
                      height={s.h}
                      loading="lazy"
                      decoding="async"
                    />
                  </picture>
                  <div className={classes.cardBody}>
                    <h3 className={classes.cardTitle}>{t(s.titleKey)}</h3>
                    <p className={classes.cardDesc}>{t(s.descKey)}</p>
                  </div>
                </div>
                  </a>
                </Link>
              </Reveal>
            </Grid>
          ))}
        </Grid>
      </Container>
    </section>
  )
}

export default BusinessServicesNew
